import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import {
  Banner,
  Footer,
  FormContainer,
  Input,
  InputPassword,
  MainContainer,
  TopHeaderBackArrow,
} from 'Components/utility';
import { ApiStatus, APP_RETURN_TIMEOUT, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import { useApiError, useAuth } from 'Hooks';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { signIn } from 'Services/auth';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

type MyFormValues = {
  email: string;
  password: string;
};

const mapStateToProps = ({ env: { deviceId, osType, osVersion, app, appVersion } }: RootState) => ({
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const Login = ({ deviceId, osType, osVersion, app, appVersion }: Props) => {
  const validationSchema = z.object({
    email: z
      .string(i18n.t('Login.content.signInForm.formField.email.errors.required'))
      .min(1, i18n.t('Login.content.signInForm.formField.email.errors.required'))
      .email(i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail')),
    password: z
      .string(i18n.t('Login.content.signInForm.formField.email.errors.required'))
      .min(1, i18n.t('Login.content.signInForm.formField.email.errors.required')),
  });

  const urlParams = new URLSearchParams(window.location.search);
  const paramsEmail = urlParams.get('email');

  const initialValues: MyFormValues = { email: paramsEmail ?? '', password: '' };

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const {
    handleLoginError,
    handleLockoutError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
    showSessionManagementError,
    showCountryRegionError,
  } = useAuth('login');

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>, resetForm: () => void, signInRequest: () => void) => {
      const data = error.response?.data
        ? error.response.data
        : {
            details:
              error.code === ErrorCode.NETWORK_ERROR
                ? ErrorCode.NETWORK_ERROR
                : i18n.t('Global.modals.errorCommunicatingServer.body'),
          };

      notifyApp('adc-webview:signin', { status: error.status, data });

      switch (error.status) {
        case ApiStatus.UNAUTHORIZED: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();

            return;
          }

          if (code === ApiStatus.SESSION_MANAGEMENT_ERROR) {
            showSessionManagementError(signInRequest);

            return;
          }

          handleLoginError(error, resetForm);
          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          handleLockoutError(error);
          resetForm();
          break;
        }

        case ApiStatus.BAD_REQUEST: {
          const code = error.response?.data.code;

          if (code === ApiStatus.UNSUPPORTED_COUNTRY || code === ApiStatus.WRONG_REGION) {
            showCountryRegionError();

            return;
          }

          showApiErrorModal();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      handleLockoutError,
      handleLoginError,
      showApiErrorModal,
      showHcpError,
      showNetworkErrorModal,
      showSessionManagementError,
      showCountryRegionError,
    ]
  );

  const signInRequest = useCallback(
    async (
      formValues: MyFormValues,
      resetForm: () => void,
      setSubmitting: (isSubmitting: boolean) => void,
      force = false
    ) => {
      setSubmitting(true);

      try {
        const { email, password } = formValues;

        const authSession = await signIn(
          email,
          password.trim(),
          osType,
          osVersion,
          app,
          appVersion,
          deviceId,
          force
        );

        store.dispatch({ type: AUTH_SESSION, authSession });
        //  Disable consents for demo
        // if (app !== 'llu5') {
        //   if (!authSession.include.patient.consents?.hipaa?.at) {
        //     mediator.publish('router:navigate', '/hipaa');
        //     return;
        //   }

        //   if (!authSession.include.patient.consents?.realWorldEvidence?.at) {
        //     mediator.publish('router:navigate', '/rwe-consent');
        //     return;
        //   }
        // }

        notifyApp<AuthSession>('adc-webview:signin', authSession);

        // This timeout is added to give the app time to redirect the user back.
        setTimeout(() => {
          setSubmitting(false);
        }, APP_RETURN_TIMEOUT);
      } catch (err) {
        setSubmitting(false);

        const error = err as AxiosError<ApiErrorData>;

        handleApiRequestError(error, resetForm, () =>
          signInRequest(formValues, resetForm, setSubmitting, true)
        );
      }
    },
    [app, appVersion, deviceId, handleApiRequestError, osType, osVersion]
  );

  const handleClickForgotPass = useCallback(() => {
    mediator.publish('router:navigate', '/forgot-password');
  }, []);

  const { handleSubmit, handleChange, handleBlur, isValid, errors, touched, values, isSubmitting } =
    useFormik({
      initialValues,
      validationSchema: toFormikValidationSchema(validationSchema),
      onSubmit: async (values, { setSubmitting, resetForm }) => {
        await signInRequest(values, resetForm, setSubmitting);
      },
      validateOnBlur: true,
      initialErrors: {
        email: i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'),
        password: i18n.t('Login.content.signInForm.formField.email.errors.required'),
      },
    });

  const appName = i18n.t('Global.appNames.lactateView');

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}
      <InformationalScreen
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('Login.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="Auth.action-go-back" />,
            label: 'Go back',
            onPress: () => {
              notifyApp('adc-webview:navigate-back-app');
            },
          },
          testID: 'Auth.actionSignInTitle',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('Login.subtitle', { appName })}</Text>
            {lockoutMessage && (
              <Banner testID="Login" message={lockoutMessage} submessage={lockoutSubmessage} />
            )}
            <FormContainer>
              <Input
                aria-label={i18n.t('Login.content.signInForm.formField.email.label')}
                placeholder={i18n.t('Login.content.signInForm.formField.email.placeholder')}
                label={i18n.t('Login.content.signInForm.formField.email.label')}
                errorMessage={errors.email}
                isInvalid={Boolean(errors.email && touched.email)}
                value={values.email}
                testID="email"
                onChangeText={handleChange('email')}
                onBlur={handleBlur('email')}
                inputMode="email"
                disabled={isLocked || !!paramsEmail}
              />
              <InputPassword
                placeholder={i18n.t('Login.content.signInForm.formField.password.placeholder')}
                label={i18n.t('Login.content.signInForm.formField.password.label')}
                isInvalid={Boolean(errors.password && touched.password)}
                errorMessage={errors.password}
                testID="password"
                onBlur={handleBlur('password')}
                onChangeText={handleChange('password')}
                defaultValue={values.password}
                disabled={isLocked}
              />
              <Stack flexDirection="column" alignItems="center">
                <Button
                  variant="tertiary"
                  label={i18n.t('Login.content.signInForm.secondaryText')}
                  onPress={handleClickForgotPass}
                  testID="Auth.forgotPassword"
                />
              </Stack>
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t('Login.content.footerCreateAccount.links.signIn.label')}
              onButtonPress={handleSubmit}
              buttonTestID="Login.signIn-btn"
              disabled={isLocked || !isValid}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(Login);
